import React, {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {Button, Dialog, Input, NavBar, TextArea, Toast} from 'antd-mobile';
import { getNavBarContainerStyle, getBottomNavBarContainerStyle } from "../../../utils/deviceUtils";
import {PhoneFill} from 'antd-mobile-icons';
import EmptyState from "../../../components/EmptyState";
import {useTranslation} from "react-i18next";
import {NativePermissions} from "../../delivery/components/PhotoNativeBridge";
import {pickupNavigationService} from "../services/PickupNavigationService";
import {PickupAppointmentType} from "../graphql/types/PickupTaskWithAppointmentsType";
import {usePickupTaskAppointment, usePickupTaskOperations} from "../store/pickupStore";
import {buildAddressFromAppointment} from "../../../utils/addressUtils";
import {getBusinessDaysText, getPickupFailureReasonText, isOnsiteContactApplicable} from "../../../enums/PickupEnum";
import {formatTimestampByLanguage, formatTodayInTimezone} from "../../../utils/dateTimeUtils";
import pickedUpDataService, {PickedUpData, PickUpBase64ToFile, uploadPhotosWithTimeout} from "../../../services/PickedUpDataService";
import {fileUpload} from "../../../utils/storageUtils";
import pickUpDraftService from "../services/PickUpDraftService";
import pickUpPhotoService, {PickUpPhotoType, PickUpPositionType} from "../services/PickUpPhotoService";
import geolocationService from "../services/PickUpGeolocationService";
import PickUpPhotoUploadCard from '../components/PickUpPhotoUploadCard';
import PickUpPhotoViewer from '../components/PickUpPhotoViewer';
import PickUpBolCard from '../components/PickUpBolCard';
import PickUpBol, {PickUpBolRef, PickUpBolSignatureRole} from '../components/PickUpBol';
import dayjs from "dayjs";
import PickUpInfoItem from "../components/PickUpInfoItem";
import {parseExceptionMessageKey} from "../../../utils/exceptionUtils";
import {navigateBack} from "../../../components/Map/MapNavigationUtils";
import {usePickupSiteV2Gray} from "../../../hooks/usePickupSiteV2Gray";
import {getUserInfo} from "../../../utils/auth";

interface MarkExceptionProps {
}

// 这几个原因司机到场且仓管在，需要仓管（海外仓）签字确认：
// 转交其他承运商(005) / 包裹未准备好(006) / 今日无货(010)。
// 其余原因现场无人可签或无需对方确认，只要司机签字
const SHIPPER_SIGNATURE_REASONS = ['ERPU005', 'ERPU006', 'ERPU010'];

const PickupFailedPage: React.FC<MarkExceptionProps> = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { appointmentId, failedReason } = useParams<{
        appointmentId: string;
        failedReason: string;
    }>();
    // 检查是否来自地图模式
    const fromMapMode = location.state?.fromMapMode || false;
    const {t, i18n} = useTranslation();
    const [photos, setPhotos] = useState<Record<PickUpPositionType, PickUpPhotoType | undefined>>({} as Record<PickUpPositionType, PickUpPhotoType | undefined>);
    const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [pickUpDetail, setPickUpDetail] = useState<PickupAppointmentType>();
    const [nextItem, setNextItem] = useState<PickupAppointmentType | null>(null);
    // 详细信息次要字段的折叠态，默认收起（与揽收成功页同构）
    const [detailExpanded, setDetailExpanded] = useState<boolean>(false);
    // 揽收备注：司机现场填写，落到 BOL 的 Notes 栏；与预约单自带的 remark 是两回事
    const [pickupNote, setPickupNote] = useState<string>('');
    // BOL 提货单：浮层显隐 + 双方签字 + 单据快照
    const [bolVisible, setBolVisible] = useState<boolean>(false);
    const [bolSignatures, setBolSignatures] = useState<Record<PickUpBolSignatureRole, string | null>>({
        shipper: null,
        driver: null,
    });
    // 单据快照在提交时按当前内容现场生成，不再需要"保存出图"这一步，也就不存在图与原因不一致
    const bolRef = useRef<PickUpBolRef>(null);
    // 出好的图暂存于此，供网络失败时随离线队列一起落盘重传
    const capturedBolImageRef = useRef<string | null>(null);
    const bolShipperRequired = SHIPPER_SIGNATURE_REASONS.includes(failedReason || '');
    // 签字当时的失败原因，作为签字有效性基准：原因一变，签字全部作废（含只需司机签字的场景）。
    // 不删数据——换回原来的原因，签字自动重新生效
    const [signedReason, setSignedReason] = useState<string | null>(null);
    const [signedRemark, setSignedRemark] = useState<string | null>(null);
    // 单据日期：签字时锁定为签字那天（见 handleBolSave）并随草稿持久化，未签字前显示 HUB 当天。
    const [signedBolDate, setSignedBolDate] = useState<string | null>(null);
    const bolDate = signedBolDate ?? formatTodayInTimezone(pickUpDetail?.hubTimezoneLocation);
    const currentReason = failedReason || '';
    // 基准含失败原因 + 揽收备注（单据的 Exception / Notes 两栏）：双方签的是那张单据的内容，
    // 任一栏变了签字即失效。备注比 trim 后的值——单据渲染 `remark || '-'`，末尾空格看不出差别
    const bolSignaturesValid = signedReason !== null
        && signedReason === currentReason
        && signedRemark === pickupNote.trim();
    const effectiveBolSignatures = bolSignaturesValid ? bolSignatures : {shipper: null, driver: null};
    // 已签过字但基准变了：卡片上常驻提示，取代一闪而过的 Toast
    const bolSignatureInvalid = !bolSignaturesValid && (!!bolSignatures.shipper || !!bolSignatures.driver);
    const bolSigned = (!bolShipperRequired || !!effectiveBolSignatures.shipper) && !!effectiveBolSignatures.driver;
    // 单据图在提交时才生成，卡片与提交门槛只看签字是否齐全有效
    const bolReady = bolSigned;

    // 浮层点「保存」才提交，此时才以当前失败原因为新基准。
    // 签完直接返回不会走到这里，基准保持原样，下次带原失败原因进来旧签字照样有效
    const handleBolSave = (signatures: Record<PickUpBolSignatureRole, string | null>) => {
        setBolSignatures(signatures);
        setSignedReason(failedReason || '');
        setSignedRemark(pickupNote.trim());
        // 单据日期 = 最后一次签字那天，按 HUB 时区算。绑签字时刻而不是进页面时刻：
        setSignedBolDate(formatTodayInTimezone(pickUpDetail?.hubTimezoneLocation));
    };
    const {pickupTaskAppointment, loading} = usePickupTaskAppointment(appointmentId || '');
    const {handlePickupFailed, submitPickupOnsiteFeedback} = usePickupTaskOperations();

    // pickup.tracking.scan.gray.switch 灰度：后端按预约单所属 HUB 算好，随任务查询一并下发。
    // 本页只有 BOL 提货单（含揽收备注，备注是单据 Notes 栏的唯一来源）跟这份名单，与揽收成功页同口径
    const trackingScanGrayEnabled = !!pickUpDetail?.trackingScanEnabled;

    // 揽收站点 2.0：揽收失败详情新增「现场联系人信息」模块（灰度 hub 展示；非必填）
    const { express2Enabled } = usePickupSiteV2Gray();
    // 按 hub 门控：仅当前 appointment 所属 hub 在灰度名单内才启用 2.0（避免"任一 hub 灰度即全量"）
    const siteV2Enabled = express2Enabled(pickUpDetail?.warehouseId);
    // 建联状态门控口径与揽收成功页一致：只对待上门/上门中/揽收成功待建联/揽收失败待建联的站点采集，
    // 已建联(4)/确认无法建联(5)/非灰度(null) 不展示
    const showOnSiteContact = siteV2Enabled && isOnsiteContactApplicable(pickUpDetail?.linkStatus);
    const [onSiteContactName, setOnSiteContactName] = useState<string>('');
    const [onSiteContactPhone, setOnSiteContactPhone] = useState<string>('');
    // 单据上的司机信息取当前登录账号
    const driverAccount = getUserInfo()?.account;
    // BOL 收货方 HUB 随任务查询一并下发，不再单独请求——否则弱网/离线下这三项会是空的
    const consignee = {
        // 优先分拨代码（如 LAX），缺失回落仓库名
        code: pickUpDetail?.hubSortingCode || pickUpDetail?.hubName || '-',
        address: pickUpDetail?.hubAddress || '-',
        contact: pickUpDetail?.hubContactName || pickUpDetail?.hubContactPhone
            ? `${pickUpDetail?.hubContactName || '-'} / ${pickUpDetail?.hubContactPhone || '-'}`
            : '-',
    };

    useEffect(() => {
        if (pickupTaskAppointment) {
            setPickUpDetail(pickupTaskAppointment);
        }
    }, [pickupTaskAppointment]);

    // 取下一站会触发列表刷新，不能依赖 pickupTaskAppointment 对象，否则刷新产生新引用又触发本 effect，形成死循环
    useEffect(() => {
        const currentStopNumber = pickUpDetail?.sequence;
        if (currentStopNumber && !nextItem) {
            pickupNavigationService.getNextPickupItem(currentStopNumber).then(setNextItem);
        }
    }, [pickUpDetail?.sequence]);


    // 草稿回填是否已完成：完成前不允许落盘，否则初始空状态会把已存的草稿冲掉
    const draftRestoredRef = useRef<boolean>(false);

    // 进页面回填草稿（每个 appointmentId 只回填一次）
    useEffect(() => {
        if (!appointmentId || draftRestoredRef.current) {
            return;
        }
        let cancelled = false;
        pickUpDraftService.getDraft(appointmentId, 'failed').then((draft) => {
            if (cancelled) {
                return;
            }
            if (draft) {
                if (draft.photos) {
                    setPhotos(draft.photos);
                }
                if (draft.bolSignatures) {
                    setBolSignatures(draft.bolSignatures);
                }
                if (draft.signedReason !== undefined) {
                    setSignedReason(draft.signedReason);
                }
                if (draft.pickupNote !== undefined) {
                    setPickupNote(draft.pickupNote);
                }
                if (draft.signedRemark !== undefined) {
                    setSignedRemark(draft.signedRemark);
                }
                // 老草稿（本字段上线前存的）没有 bolDate，回落为 HUB 当天
                if (draft.bolDate) {
                    setSignedBolDate(draft.bolDate);
                }
                if (draft.onSiteContactName !== undefined) {
                    setOnSiteContactName(draft.onSiteContactName);
                }
                if (draft.onSiteContactPhone !== undefined) {
                    setOnSiteContactPhone(draft.onSiteContactPhone);
                }
            }
            draftRestoredRef.current = true;
        });
        return () => {
            cancelled = true;
        };
    }, [appointmentId, failedReason, t]);

    // 表单任一项变化即落盘；全空时不建草稿，避免只是点进来看一眼也留记录
    useEffect(() => {
        if (!appointmentId || !draftRestoredRef.current) {
            return;
        }
        const hasContent = Object.values(photos).some((photo) => photo?.url)
            || pickupNote !== ''
            || !!bolSignatures.shipper
            || !!bolSignatures.driver
            || onSiteContactName !== ''
            || onSiteContactPhone !== '';
        if (!hasContent) {
            // 表单被清空时必须把本地草稿一并删掉：只 return 不落盘，旧草稿会留在 KVStore 里，退出重进又恢复出已清掉的内容
            void pickUpDraftService.removeDraft(appointmentId, 'failed');
            return;
        }
        pickUpDraftService.saveDraft({
            appointmentId,
            type: 'failed',
            updatedAt: Date.now(),
            photos,
            pickupNote,
            bolSignatures,
            signedReason,
            signedRemark,
            bolDate,
            onSiteContactName,
            onSiteContactPhone,
        });
    }, [appointmentId, photos, pickupNote, bolSignatures, signedReason, signedRemark, bolDate, onSiteContactName, onSiteContactPhone]);

    useEffect(() => {
        const requestPermissions = async () => {
            try {
                await NativePermissions.request('camera');
                await NativePermissions.request('storage');
            } catch (error) {
                console.error('Failed to request permissions:', error);
            }
        };

        requestPermissions();
    }, []);

    /*照片相关操作 start======*/

    // 拍照或从相册选择
    const takePhoto = async (type: PickUpPositionType, file: File) => {
        try {
            setIsLoading(true);
            await pickUpPhotoService.handlePhotoNotUpload(type, file, (updateFn) => {
                setPhotos((prev) => updateFn(prev));
            });
        } catch (error) {
            console.error('Failed to take photo:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // 删除照片
    const deletePhoto = (type: PickUpPositionType) => {
        Dialog.confirm({
            content: t('pages.pickUp.photo.deleteContent'),
            confirmText: t('pages.pickUp.photo.deleteConfirm'),
            cancelText: t('pages.pickUp.photo.deleteCancel'),
            onConfirm: () => {
                pickUpPhotoService.handleDeletePhoto(type, (updateFn) => {
                    setPhotos((prev) => updateFn(prev));
                });
            },
        });
    };

    // 查看大图
    const handleViewPhoto = (url: string) => setViewingPhoto(url);

    /*照片相关操作 end======*/

    // 保存订单数据到本地
    const savePickedUpToLocal = async (): Promise<boolean> => {
        try {
            const pickedUpData: PickedUpData = {
                updatedAt: Date.now(),
                photos: Object.entries(photos || {})
                    .map(([type, photo]) => ({
                        type: type as PickUpPositionType,
                        photo: photo as PickUpPhotoType
                    })),
                id: pickUpDetail?.id?.toString() || '',
                type: 'failed',
                stopNumber: pickUpDetail?.sequence?.toString(),
                channelName: pickUpDetail?.channelName,
                taskId: pickUpDetail?.taskId || '',
                failedReason: failedReason || '',
                cooperationWarehouseId: pickUpDetail?.cooperationWarehouseId,
                // 弱网重传时同样要带上，否则备注、签字与单据图会静默丢失
                driverRemark: pickupNote.trim(),
                bolSignatures,
                bolImage: capturedBolImageRef.current,
            };

            // 保存到本地
            const saved = await pickedUpDataService.savePickedUpData(pickedUpData);
            return saved;
        } catch (error) {
            console.error('Failed to save PickedUp to local:', error);
            return false;
        }
    };

    // 处理揽收失败
    const handlePickupFailedMutation = async () => {
        setIsLoading(true);
        try {
            if (!photos.signFor?.objectKey || !photos.packageImg?.objectKey || !photos.warehousePhoto?.objectKey) {
                Toast.show({content: t('pages.pickUp.finished.toast.needPhotos'), position: 'center'});
                return;
            }
            // 揽收站点 2.0：现场联系方式非必填，但填了必须 10 位（合规校验）→ 拦在 1.0 落库前
            if (showOnSiteContact && onSiteContactPhone !== '' && onSiteContactPhone.length !== 10) {
                Toast.show({ content: t('pages.pickUp.onSiteContact.phoneInvalid'), position: 'center' });
                setIsLoading(false);
                return;
            }

            // 揽收失败动作时刻取一次实时定位（供 POP 审核判揽收点与站点距离）；
            // 取位失败/无权限则留空，不写 0.0、不阻断提交（后端据空值识别"无定位"）。
            let pickupLatitude: number | undefined;
            let pickupLongitude: number | undefined;
            try {
                const pos = await geolocationService.getFreshPosition();
                pickupLatitude = pos.latitude;
                pickupLongitude = pos.longitude;
                console.log('[PickupFailed] 取位成功', pickupLatitude, pickupLongitude);
            } catch (e) {
                console.warn('[PickupFailed] 取位失败，经纬度留空提交', e);
            }

            const currentPhotos = {...photos};
            // 先出图再走网络：出图是纯本地操作，放在任何网络调用之后的话，
            // 离线时会因照片上传先抛错而根本执行不到，导致离线队列里没有单据图、重传后 BOL 丢失
            // 灰度未命中时浮层根本没渲染，bolRef.current 为 null，不能进这段
            let bolImageDataUrl: string | null = null;
            if (trackingScanGrayEnabled) {
                try {
                    bolImageDataUrl = await bolRef.current!.captureImage();
                } catch (captureError) {
                    // 出图失败属渲染问题、重试无用，直接提示并中止
                    console.error('Failed to capture BOL snapshot:', captureError);
                    Toast.show({content: t('pages.pickUp.pickedUp.bolCaptureFailed'), position: 'center'});
                    return;
                }
                capturedBolImageRef.current = bolImageDataUrl;
            }

            await handlePickupFailed({
                appointmentId: pickUpDetail?.id || '',
                taskId: pickUpDetail?.taskId || '',
                failedReason: failedReason || '',
                exceptionPhotosS3KeysJson: [photos?.signFor?.objectKey, photos?.packageImg?.objectKey,photos?.warehousePhoto?.objectKey],
                actualPickupDate:dayjs().format("YYYY-MM-DD"),
                actualPickupTimeEnd:dayjs().format("HH:mm:ss"),
                cooperationWarehouseId: pickUpDetail?.cooperationWarehouseId,
                latitude: pickupLatitude,
                longitude: pickupLongitude,
            }, true);

            setPhotos(currentPhotos);
            // 图片上传，带超时控制
            const photoKeys = await uploadPhotosWithTimeout(
                Object.values(photos)
                    .filter((item): item is PickUpPhotoType => item?.objectKey !== undefined && item?.url !== undefined)
            );

            const bolS3Key = bolImageDataUrl
                ? (await fileUpload(await PickUpBase64ToFile(bolImageDataUrl, `bol_${Date.now()}.jpeg`), 'bol')).objectKey
                : undefined;

            await handlePickupFailed({
                appointmentId: pickUpDetail?.id || '',
                taskId: pickUpDetail?.taskId || '',
                failedReason: failedReason || '',
                exceptionPhotosS3KeysJson: photoKeys,
                actualPickupDate:dayjs().format("YYYY-MM-DD"),
                actualPickupTimeEnd:dayjs().format("HH:mm:ss"),
                cooperationWarehouseId: pickUpDetail?.cooperationWarehouseId,
                latitude: pickupLatitude,
                longitude: pickupLongitude,
                bolS3Key,
                driverRemark: trackingScanGrayEnabled ? pickupNote.trim() : undefined,
            }, false);

            // 揽收站点 2.0 灰度 hub：追加现场回传（现场联系人非必填 + 推站点建联状态）。
            // best-effort：失败结果已由上面的 1.0 通路落库，这里失败不回滚、不阻断主流程，仅 toast 提示。
            if (siteV2Enabled && pickUpDetail?.id) {
                try {
                    await submitPickupOnsiteFeedback({
                        pickupAppointmentId: pickUpDetail.id,
                        pickupSuccess: false,
                        onsiteContactName: onSiteContactName.trim(),
                        onsiteContactPhone: onSiteContactPhone,
                        photosS3KeysJson: JSON.stringify(photoKeys),
                        // 上门备注：揽收失败复用失败原因（→ op5 content.note）
                        note: failedReason || undefined,
                    });
                } catch (feedbackError) {
                    console.error('submitPickupOnsiteFeedback failed (non-blocking):', feedbackError);
                    Toast.show({ content: t('pages.pickUp.onSiteContact.submitFailed'), position: 'center' });
                }
            }

            // 已提交成功，草稿使命结束
            await pickUpDraftService.removeDraftsByAppointment(appointmentId || '');

            console.log('History length:', window.history.length);
            console.log('Current pathname:', window.location.pathname);
            console.log('fromMapMode:', fromMapMode);
            if (nextItem && !fromMapMode) {
                console.log('详情下一条数据');
                navigate(`/pickup/picking-up/detail/${nextItem.id}/${nextItem.status}`);
            } else if (fromMapMode) {
                console.log('返回到地图');
                navigateBack.navigateToLsatPage(navigate);
            } else {
                console.log('返回到列表');
                navigateBack.navigateToPickupList(navigate);
            }
        } catch (error: any) {
            if (error.networkError) {
                // 数据已转入重传队列，草稿不再需要（留着会在重传成功后又被回填出来）
                await savePickedUpToLocal();
                await pickUpDraftService.removeDraftsByAppointment(appointmentId || '');
                if (nextItem && !fromMapMode) {
                    navigate(`/pickup/picking-up/detail/${nextItem.id}/${nextItem.status}`);
                } else if (fromMapMode) {
                    navigateBack.navigateToLsatPage(navigate);
                } else {
                    navigateBack.navigateToPickupList(navigate);
                }
            } else {
                console.error('Failed to complete PickupFailedPage:', error);
                const messageKey = parseExceptionMessageKey(error);
                if (messageKey === 'errors.pickup.cancelledAppointmentDotUpdate'){
                    Toast.show({ content: t(messageKey), position: 'center' });
                }else {
                    Toast.show({content: 'Mark failed, please try again', position: 'center'});
                }
            }
        } finally {
            setIsLoading(false);
        }
    };

    // 拨打电话
    const handleCall = () => {
        if (!pickUpDetail?.contactPhone || pickUpDetail.contactPhone.trim() === '') {
            Toast.show({
                content: t('pages.delivery.orderDetail.noPhoneNumber'),
                position: 'bottom',
            });
            return;
        }

        // 清理电话号码，只保留数字和+号
        const cleanPhone = pickUpDetail.contactPhone.replace(/[^\d+]/g, '');

        if (!cleanPhone) {
            Toast.show({
                content: t('pages.delivery.orderDetail.invalidPhoneNumber'),
                position: 'bottom',
            });
            return;
        }

        try {
            window.location.href = `tel:${cleanPhone}`;
        } catch (error) {
            console.error('Phone call failed:', error);
            Toast.show({
                content: t('pages.delivery.orderDetail.phoneCallFailed', {phone: cleanPhone}),
                position: 'bottom',
                duration: 3000
            });
        }
    };

    if (!pickUpDetail) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(-1)}>
                        {t('pages.pickUp.pickUpFailed.title')}
                    </NavBar>
                </div>
                <EmptyState/>
            </div>
        );
    }
    const businessDaysStr = getBusinessDaysText(pickUpDetail.businessDays, i18n.language);
    return (
        <div className="flex flex-col h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={() => navigate(-1)}>{t('pages.pickUp.pickUpFailed.title')}</NavBar>
            </div>

            <div className="flex-1 overflow-y-auto">
                {/* shadow-sm 不能省：与下一张卡之间靠 mb-1 的间隙 + 投影分隔，
                    没有投影两块白底会糊成一片（本页其余卡片都带 shadow-sm，此处原先漏了） */}
                <div className="mb-1 bg-white p-4 shadow-sm">
                    <h2 className="text-base font-semibold mb-1">{t('pages.pickUp.pickUpFailed.detailInformation')}</h2>
                    <div className="mb-1">
                        <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.name')} value={pickUpDetail.channelName}/>
                        <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.address')}
                                  value={buildAddressFromAppointment(pickUpDetail)}/>
                        <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.appointmentTime')}
                                  value={formatTimestampByLanguage(pickUpDetail?.appointmentTime, i18n.language)}/>
                        <div className="flex justify-between py-2 border-gray-100">
                            <span className="text-gray-600 text-sm">{t('pages.pickUp.pickUpFailed.failedReason')}:</span>
                            <span
                                className="text-sm font-medium text-right text-[#6366f1]">{getPickupFailureReasonText(failedReason || '', i18n.language)}</span>
                        </div>
                        <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.remark')} value={pickUpDetail?.remark || '-'} />
                        {/* 次要字段默认折叠，展开后按 站点编号/联系人/电话/营业时间 顺序补全 */}
                        <div
                            className="flex items-center justify-center gap-1 py-2 text-sm"
                            style={{color: 'var(--adm-color-primary)'}}
                            onClick={() => setDetailExpanded((prev) => !prev)}
                        >
                            <span>{t(`pages.pickUp.pickedUp.${detailExpanded ? 'collapseMore' : 'expandMore'}`)}</span>
                            {/* 扁平宽角箭头：用 svg 而非文字符号，避免字形基线偏移和模糊 */}
                            <svg width="13" height="7" viewBox="0 0 13 7" fill="none" className="shrink-0">
                                <path
                                    d={detailExpanded ? 'M1 6l5.5-4.5L12 6' : 'M1 1l5.5 4.5L12 1'}
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                        {detailExpanded && (
                            <>
                                <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.number')}
                                          value={pickUpDetail?.sequence?.toString()}/>
                                <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.contact')} value={pickUpDetail?.contactName}/>
                                <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.phone')}
                                          value={`${pickUpDetail?.contactPhone}${pickUpDetail?.ext ? `-${pickUpDetail?.ext}` : ''}`}/>
                                <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.businessHours')} value={businessDaysStr}/>
                                <div className="flex justify-between py-2 border-gray-100">
                                    <span className="text-gray-600 text-sm"></span>
                                    <span className="text-sm font-medium text-right">{pickUpDetail?.businessHours}</span>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* 揽收备注：选填，落到 BOL 的 Notes 栏（不是预约单自带的备注）。与揽收成功页同构，跟 BOL 同一份灰度 */}
                {trackingScanGrayEnabled && (
                <div className="mb-1 bg-white p-4 shadow-sm">
                    <div className="text-sm text-gray-500 mb-1.5">{t('pages.pickUp.pickedUp.pickupNote')}:</div>
                    <TextArea
                        placeholder={t('pages.pickUp.pickedUp.pickupNotePlaceholder') as string}
                        value={pickupNote}
                        onChange={setPickupNote}
                        maxLength={200}
                        rows={2}
                        className="border border-gray-300 rounded-md px-2 py-1"
                    />
                </div>
                )}

                {/* BOL 提货单入口（灰度 hub 才展示）。位置与揽收成功页对齐：紧跟揽收备注之后 */}
                {trackingScanGrayEnabled && (
                <PickUpBolCard
                    signed={bolReady}
                    note={bolSignatureInvalid ? String(t('pages.pickUp.pickedUp.bolSignatureInvalidTip')) : undefined}
                    onClick={() => setBolVisible(true)}
                />
                )}

                {/* 现场联系人信息（揽收站点 2.0 · 揽收失败新增模块，灰度 hub + 建联流程中的站点展示，非必填） */}
                {showOnSiteContact && (
                    <div className="mb-1 bg-white p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                            <h2 className="text-base font-semibold">{t('pages.pickUp.onSiteContact.title')}</h2>
                            <span className="text-xs text-green-600 bg-green-50 rounded px-1.5 py-0.5">
                                {t('pages.pickUp.onSiteContact.new')}
                            </span>
                        </div>
                        <div className="mb-2">
                            <div className="text-sm mb-1">{t('pages.pickUp.onSiteContact.contactName')}</div>
                            <Input
                                placeholder={t('pages.pickUp.onSiteContact.contactNamePlaceholder') as string}
                                value={onSiteContactName}
                                onChange={setOnSiteContactName}
                                className="border border-gray-300 rounded-md h-10 px-2"
                            />
                        </div>
                        <div>
                            <div className="text-sm mb-1">{t('pages.pickUp.onSiteContact.contactPhone')}</div>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600 border border-gray-300 rounded-md h-10 flex items-center px-2">+1</span>
                                <Input
                                    placeholder={t('pages.pickUp.onSiteContact.contactPhonePlaceholder') as string}
                                    value={onSiteContactPhone}
                                    onChange={(v) => setOnSiteContactPhone(v.replace(/\D/g, '').slice(0, 10))}
                                    type="tel"
                                    className="border border-gray-300 rounded-md h-10 px-2 flex-1"
                                />
                            </div>
                            {onSiteContactPhone !== '' && onSiteContactPhone.length !== 10 && (
                                <div className="text-xs text-red-500 mt-1">{t('pages.pickUp.onSiteContact.phoneInvalid')}</div>
                            )}
                        </div>
                    </div>
                )}

                <div className="mb-2 bg-white p-4 border-t-1 shadow-sm">
                    <h2 className="text-base font-semibold mb-1">{t('pages.pickUp.pickUpFailed.photos')}</h2>
                    <p className="text-sm text-gray-600 mb-1">
                        {t('pages.pickUp.pickUpFailed.photoDesc')}
                        <span className="text-green-600 text-base">{t('pages.pickUp.pickUpFailed.photoDescDetail')}</span>
                        {t('pages.pickUp.pickUpFailed.punctuation')}
                    </p>
                    <div className="grid grid-cols-3 gap-4">
                        <PickUpPhotoUploadCard
                            photoUrl={photos.signFor?.url}
                            type="signFor"
                            description=""
                            onTakePhoto={takePhoto}
                            onDelete={deletePhoto}
                            onView={handleViewPhoto}
                        />
                        <PickUpPhotoUploadCard
                            photoUrl={photos.packageImg?.url}
                            type="packageImg"
                            description=""
                            onTakePhoto={takePhoto}
                            onDelete={deletePhoto}
                            onView={handleViewPhoto}
                        />
                        <PickUpPhotoUploadCard
                            photoUrl={photos.warehousePhoto?.url}
                            type="warehousePhoto"
                            description=""
                            onTakePhoto={takePhoto}
                            onDelete={deletePhoto}
                            onView={handleViewPhoto}
                        />
                    </div>
                </div>

                {/* 查看大图 */}
                <PickUpPhotoViewer
                    photoUrl={viewingPhoto}
                    onClose={() => setViewingPhoto(null)}
                />
            </div>

            {/* BOL 提货单全屏浮层：失败单没有数量单位，货物明细位置改展示失败原因。
                灰度 hub 才挂载；不挂载时 bolRef 为空，提交处已同步门控 */}
            {trackingScanGrayEnabled && (
            <PickUpBol
                visible={bolVisible}
                onClose={() => setBolVisible(false)}
                bolNumber={pickUpDetail?.appointmentNumber ? `BOL${pickUpDetail.appointmentNumber}` : '-'}
                date={bolDate}
                pickup={{
                    // 站点ID 取揽收站点（合作仓）；warehouseId 是收货方 HUB，不是提货方
                    code: pickUpDetail?.cooperationWarehouseId || '-',
                    address: buildAddressFromAppointment(pickUpDetail),
                    contact: `${pickUpDetail?.contactName || '-'} / ${pickUpDetail?.contactPhone || '-'}`,
                }}
                consignee={consignee}
                exceptionReason={getPickupFailureReasonText(failedReason || '', i18n.language)}
                shipperRequired={bolShipperRequired}
                driverName={driverAccount?.name || '-'}
                driverPhone={driverAccount?.phone || '-'}
                licensePlate={pickUpDetail?.licensePlate || '-'}
                remark={pickupNote}
                shipperSignature={effectiveBolSignatures.shipper}
                driverSignature={effectiveBolSignatures.driver}
                ref={bolRef}
                onSave={handleBolSave}
            />
            )}

            <div {...getBottomNavBarContainerStyle('flex-0 flex px-4 py-2 mt-auto gap-4')}>
                <div className="flex-0">
                    <Button
                        shape="rounded"
                        className="w-12 h-12 flex items-center justify-center"
                        onClick={handleCall}
                        style={{'--background-color': '#f0f0f0'}}
                    >
                        <PhoneFill className="text-xl"/>
                    </Button>
                </div>
                <div className="flex-1">
                    <Button
                        block
                        color="primary"
                        loading={isLoading}
                        disabled={!photos.signFor?.url || !photos.packageImg?.url ||!photos.warehousePhoto?.url || isLoading || (trackingScanGrayEnabled && !bolReady)}
                        onClick={handlePickupFailedMutation}
                    >
                        {t('pages.pickUp.pickUpFailed.complete')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default PickupFailedPage;
